/**
 * C6 employee-page view-model — pure display shaping (avatar initials, LTR national-id
 * grouping, claim status→tag mapping, newest-first sort, quick-search filter, and the
 * remaining-annual-cap calculation) kept out of the .vue so node:test can cover the logic
 * directly (the companies repo has no SFC test runner). The template just binds the output.
 */
var CLAIM_TAG = {
  new: 'new',
  review: 'review',
  pending_docs: 'muted',
  approved: 'ok',
  rejected: 'danger',
};

/** First two characters of the (space-stripped) name → avatar monogram (mockup: "أح"). */
function initials(fullName) {
  var chars = Array.from(String(fullName || '').replace(/\s+/g, ''));
  return chars.length ? chars.slice(0, 2).join('') : '—';
}

/**
 * Group a national id into fixed-size blocks for readable LTR display (default 4).
 * §4 returns national ids already MASKED ("****last4"), so any value that is not
 * purely digits is treated as an OPAQUE display token and returned verbatim — the
 * mask is never stripped or re-grouped. (Stripping the "****" and re-grouping the
 * trailing digits would drop the mask indicator, and on a backend regression could
 * render a raw id grouped as if it were intentional cleartext.)
 */
function groupDigits(value, size) {
  var raw = String(value == null ? '' : value);
  if (!raw) return '';
  if (/\D/.test(raw)) return raw; // masked / non-digit → opaque, never reformatted
  var n = size || 4;
  var groups = [];
  for (var i = 0; i < raw.length; i += n) groups.push(raw.slice(i, i + n));
  return groups.join(' ');
}

/** §2 claim status → .tag modifier class. */
function claimTagClass(status) {
  return CLAIM_TAG[status] || 'muted';
}

/** Newest-first copy of the claims list (invoiceDate, falling back to createdAt). */
function sortByDateDesc(claims, field) {
  var f = field || 'invoiceDate';
  return (claims || []).slice().sort(function (a, b) {
    var av = a[f] || a.createdAt || '';
    var bv = b[f] || b.createdAt || '';
    if (av < bv) return 1;
    if (av > bv) return -1;
    return 0;
  });
}

/** Quick-search filter over serviceType / invoiceNo / provider / amount. */
function filterClaims(claims, q) {
  var rows = claims || [];
  var needle = String(q || '').trim().toLowerCase();
  if (!needle) return rows;
  return rows.filter(function (c) {
    var hay = [c.serviceType, c.invoiceNo, c.provider, String(c.amount)].join(' ').toLowerCase();
    return hay.indexOf(needle) !== -1;
  });
}

/** Remaining annual cap = cap − Σ decidedAmount of approved claims. */
function remainingCap(annualCap, claims) {
  var cap = Number(annualCap) || 0;
  var used = 0;
  (claims || []).forEach(function (c) {
    if (c.status === 'approved') {
      var amt = c.decidedAmount != null ? Number(c.decidedAmount) : Number(c.amount) || 0;
      used += amt;
    }
  });
  return { cap: cap, used: used, remaining: cap - used };
}

function pickNum(obj, keys) {
  if (!obj) return null;
  for (var i = 0; i < keys.length; i++) {
    var v = obj[keys[i]];
    if (v != null && v !== '' && isFinite(Number(v))) return Number(v);
  }
  return null;
}

/**
 * Coverage / consumption exactly as the API reports it — NEVER derived from the
 * claims list (that list is paginated/filtered and would fabricate numbers).
 * Looks at employee.coverage | usage | policyUsage, policy.usage, then flat
 * employee fields. Returns { cap, consumed, remaining } (each number|null) or
 * null when the API provides nothing; remaining is only computed (cap - consumed)
 * when both of those come from the API.
 */
function coverageUsage(employee, policy) {
  var e = employee || {};
  // GET /employees/:id `coverageUsage` {annualCap, consumed, remaining} | null is the real source.
  var sources = [e.coverageUsage, e.coverage, e.usage, e.policyUsage, policy && policy.usage, e];
  var cap = null;
  var consumed = null;
  var remaining = null;
  for (var i = 0; i < sources.length; i++) {
    var src = sources[i];
    if (!src || typeof src !== 'object') continue;
    if (cap == null) cap = pickNum(src, ['annualCap', 'cap', 'limit', 'limitAmount']);
    if (consumed == null) consumed = pickNum(src, ['consumed', 'consumedAmount', 'used', 'usedAmount']);
    if (remaining == null) remaining = pickNum(src, ['remaining', 'remainingAmount']);
  }
  if (consumed == null && remaining == null) return null;
  if (remaining == null && cap != null) remaining = cap - consumed;
  return { cap: cap, consumed: consumed, remaining: remaining };
}

/** Consumed share of the cap, clamped to 0..100 (null without a positive cap). */
function usagePercent(usage) {
  if (!usage || !(usage.cap > 0) || usage.consumed == null) return null;
  return Math.max(0, Math.min(100, Math.round((usage.consumed / usage.cap) * 100)));
}

module.exports = {
  usagePercent: usagePercent,
  coverageUsage: coverageUsage,
  CLAIM_TAG: CLAIM_TAG,
  initials: initials,
  groupDigits: groupDigits,
  claimTagClass: claimTagClass,
  sortByDateDesc: sortByDateDesc,
  filterClaims: filterClaims,
  remainingCap: remainingCap,
};
